import {
  Box,
  ListItemIcon,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  Paper,
  Typography,
  Theme,
} from "@mui/material";
import { Check as CheckIcon } from "@mui/icons-material";
import { useGetCommentsByBugIdQuery } from "@/features/bughurd/api";
import { useEffect, useState } from "react";
import AssigneePickerPopover from "./AssigneePickerPopover";
import { DatePickerPopover } from "./DatePickerPopover";
import CommentsPopover from "./CommentsPopover";
import { useTranslation } from "react-i18next";

interface User {
  _id: string;
  fullName: string;
  [key: string]: any;
}

interface StatusOption {
  _id: string;
  status: string;
  [key: string]: any;
}

interface SeverityOption {
  value: string;
  label: string;
  icon: React.ReactNode;
}

interface TypeOption {
  code: string;
  label: string;
}

interface Task {
  _id: string;
  severity?: string;
  bugStatus?: string;
  dueDate?: string;
  assignTo?: User[];
  type?: string;
  [key: string]: any;
}

interface EditPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  fieldType: string | null;
  editingTask: Task | null;
  onUpdate: (value: any) => void;
  theme: Theme;
  t: (key: string) => string;
  severityOptions: SeverityOption[];
  statusOptions: StatusOption[];
  isLoadingStatusLabel: boolean;
  users: User[];
  selectedAssignees: User[];
  handleAssigneeChange: (event: any, newValue: User[]) => void;
  handleAssigneeBlur: () => void;
  isUpdating: boolean;
  typeOptions: TypeOption[];
}

const EditPopover = ({
  open,
  anchorEl,
  onClose,
  fieldType,
  editingTask,
  onUpdate,
  theme,
  t,
  severityOptions,
  statusOptions,
  isLoadingStatusLabel,
  users,
  selectedAssignees,
  handleAssigneeChange,
  handleAssigneeBlur,
  isUpdating,
  typeOptions,
}: EditPopoverProps) => {
  const { t: translate } = useTranslation(["bugherd"]);

  let popoverContent: React.ReactNode;
  let popoverWidth = fieldType === "commentCount" ? 450 : 250;
  const {
    data: commentsData,
    isLoading: isLoadingComments,
    isFetching,
  } = useGetCommentsByBugIdQuery(
    { bugId: editingTask?._id },
    {
      skip: !editingTask?._id,
      refetchOnMountOrArgChange: true,
    }
  );

  // For date picker
  const [dateAnchorEl, setDateAnchorEl] = useState<HTMLElement | null>(null);

  // For assignee picker
  const [assigneeAnchorEl, setAssigneeAnchorEl] = useState<HTMLElement | null>(
    null
  );

  // Handle date select from the DatePickerPopover
  const handleDateSelect = (date: string) => {
    onUpdate(date);
  };

  // Handle assignee change from AssigneePickerPopover
  const handleAssigneeUpdate = (newAssignees: User[]) => {
    // Update the assignees and trigger the API update
    const assigneeIds = newAssignees.map((user) => user._id);
    onUpdate(assigneeIds);
  };

  // Set anchor elements when fields are activated
  useEffect(() => {
    if (open && anchorEl) {
      if (fieldType === "dueDate") {
        setDateAnchorEl(anchorEl);
        setAssigneeAnchorEl(null);
      } else if (fieldType === "assignTo") {
        setAssigneeAnchorEl(anchorEl);
        setDateAnchorEl(null);
      } else {
        setDateAnchorEl(null);
        setAssigneeAnchorEl(null);
      }
    } else {
      setDateAnchorEl(null);
      setAssigneeAnchorEl(null);
    }
  }, [open, fieldType, anchorEl]);

  switch (fieldType) {
    case "severity":
      popoverContent = (
        <List
          sx={{
            width: "100%",
            padding: 0,
            "& .MuiListItem-root": {
              padding: 0,
            },
          }}
        >
          {severityOptions.map((option) => (
            <ListItem key={option.value} disablePadding>
              <ListItemButton
                selected={editingTask?.severity === option.value}
                onClick={() => onUpdate(option.value)}
                sx={{
                  py: 1,
                  "&.Mui-selected": {
                    backgroundColor: "rgba(25, 118, 210, 0.08)",
                  },
                  "&:hover": {
                    backgroundColor: "rgba(25, 118, 210, 0.04)",
                  },
                }}
              >
                <Box display="flex" alignItems="center" gap={1} width="100%">
                  <ListItemIcon
                    sx={{
                      minWidth: "auto",
                      minHeight: "auto",
                      width: 20,
                      height: 20,
                      marginRight: 1,
                    }}
                  >
                    {option.icon}
                  </ListItemIcon>
                  <ListItemText primary={translate(option.label)} />
                  {editingTask?.severity === option.value && (
                    <CheckIcon
                      sx={{
                        color: theme.palette.primary.main,
                        fontSize: 18,
                        marginLeft: "auto",
                      }}
                    />
                  )}
                </Box>
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      );
      break;

    case "bugStatus":
      popoverContent = (
        <List
          sx={{
            width: "100%",
            padding: 0,
            "& .MuiListItem-root": {
              padding: 0,
            },
          }}
        >
          {isLoadingStatusLabel ? (
            <ListItem>
              <ListItemText primary={translate("Loading Statuses")} />
            </ListItem>
          ) : (
            statusOptions?.map((option) => (
              <ListItem key={option._id} disablePadding>
                <ListItemButton
                  selected={editingTask?.bugStatus === option._id}
                  onClick={() => onUpdate(option._id)}
                  sx={{
                    py: 1,
                    "&.Mui-selected": {
                      backgroundColor: "rgba(25, 118, 210, 0.08)",
                    },
                    "&:hover": {
                      backgroundColor: "rgba(25, 118, 210, 0.04)",
                    },
                  }}
                >
                  <ListItemText primary={option.status} />
                  {editingTask?.bugStatus === option._id && (
                    <CheckIcon
                      sx={{
                        color: theme.palette.primary.main,
                        fontSize: 18,
                        marginLeft: "auto",
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            ))
          )}
        </List>
      );
      break;

    case "type":
      popoverContent = (
        <List
          sx={{
            width: "100%",
            padding: 0,
            "& .MuiListItem-root": {
              padding: 0,
            },
          }}
        >
          {typeOptions?.map((option) => (
            <ListItem key={option.code} disablePadding>
              <ListItemButton
                selected={
                  (editingTask?.type === "BUG" && option.code === "DEFECT") ||
                  editingTask?.type === option.code
                }
                onClick={() => onUpdate(option.code)}
                sx={{
                  py: 1,
                  "&.Mui-selected": {
                    backgroundColor: "rgba(25, 118, 210, 0.08)",
                  },
                  "&:hover": {
                    backgroundColor: "rgba(25, 118, 210, 0.04)",
                  },
                }}
              >
                <ListItemText primary={option.label} />
                {(editingTask?.type === option.code ||
                  (editingTask?.type === "BUG" &&
                    option.code === "DEFECT")) && (
                  <CheckIcon
                    sx={{
                      color: theme.palette.primary.main,
                      fontSize: 18,
                      marginLeft: "auto",
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      );
      break;

    case "dueDate":
      // For the due date field, we'll render nothing in the regular popover
      // since we're using the DatePickerPopover instead
      popoverContent = null;
      break;

    case "assignTo":
      // For the assignTo field, we'll render nothing in the regular popover
      // since we're using the AssigneePickerPopover instead
      popoverContent = null;
      break;

    case "commentCount":
      popoverContent = (
        <CommentsPopover
          anchorEl={anchorEl}
          onClose={onClose}
          comments={commentsData?.data?.decryptedData?.items || []}
          taskId={editingTask?._id}
          isLoading={isLoadingComments || isFetching}
          taskName={editingTask?.summary || ""}
        />
      );
      break;

    default:
      popoverContent = null;
  }

  // For the due date, we use DatePickerPopover instead of the regular popover content
  if (fieldType === "dueDate" && dateAnchorEl) {
    return (
      <DatePickerPopover
        anchorEl={dateAnchorEl}
        onClose={onClose}
        selectedDate={editingTask?.dueDate || null}
        onDateSelect={handleDateSelect}
      />
    );
  }

  // For assignee selection, we use AssigneePickerPopover
  if (fieldType === "assignTo" && assigneeAnchorEl) {
    return (
      <AssigneePickerPopover
        anchorEl={assigneeAnchorEl}
        onClose={onClose}
        users={users || []}
        assignedUsers={selectedAssignees || []}
        onAssigneeChange={handleAssigneeUpdate}
      />
    );
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      PaperProps={{
        elevation: 3,
        sx: {
          borderRadius: 1,
          overflow: "visible",
        },
      }}
    >
      <Paper
        sx={{
          p: fieldType === "commentCount" ? 0 : 0,
          width: popoverWidth,
        }}
      >
        {fieldType === "severity" && (
          <Typography
            variant="subtitle2"
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.default,
              color: "text.secondary",
            }}
          >
            {translate("Select Priority")}
          </Typography>
        )}

        {fieldType === "bugStatus" && (
          <Typography
            variant="subtitle2"
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.default,
              color: "text.secondary",
            }}
          >
            {translate("Select Status")}
          </Typography>
        )}

        {fieldType === "type" && (
          <Typography
            variant="subtitle2"
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.default,
              color: "text.secondary",
            }}
          >
            {translate("Select Type")}
          </Typography>
        )}

        {popoverContent}
      </Paper>
    </Popover>
  );
};

export default EditPopover;
